import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  BadgeCheck,
  Check,
  ChevronRight,
  ClipboardList,
  Download,
  Edit3,
  FileCheck2,
  HandHeart,
  Landmark,
  Megaphone,
  ReceiptText,
  RefreshCcw,
  ShieldCheck,
  UserCheck,
  Users
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { motion } from "framer-motion";
import { candidateStore } from "./mockApi";
import { candidateApi } from "./apiClient";
import type { CandidateProfile, CandidateSubmission, DonationIntentResult, DonationReadiness, DonationSubmission } from "./types";

type Screen = "public" | "onboarding" | "dashboard" | "admin" | "donations" | "legal";

const emptySubmission: CandidateSubmission = {
  name: "",
  office: "",
  district: "",
  location: "",
  party: "Nonpartisan",
  committeeName: "",
  treasurerName: "",
  email: "",
  story: "",
  quote: "",
  priorities: ["", "", ""],
  updateTitle: "",
  updateBody: ""
};

const complianceSteps = [
  ["Verify", "Candidate identity, office sought, committee details, and review status.", ShieldCheck],
  ["Publish", "Approved profiles render publicly with committee identity and campaign updates.", ClipboardList],
  ["Capture", "Supporters can follow a candidate before donations launch.", FileCheck2],
  ["Audit", "Admin review decisions and profile changes stay explicit in the record.", Megaphone]
] as const;

const phaseScreens: Array<[string, Screen, LucideIcon]> = [
  ["Public profiles", "public", UserCheck],
  ["Candidate onboarding", "onboarding", Edit3],
  ["Candidate dashboard", "dashboard", Users],
  ["Admin review", "admin", ShieldCheck],
  ["Donation prep", "donations", ReceiptText],
  ["Legal placeholders", "legal", FileCheck2]
];

export function Phase1App() {
  const [screen, setScreen] = useState<Screen>("public");
  const [candidates, setCandidates] = useState<CandidateProfile[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [apiNotice, setApiNotice] = useState("Loading Bliss backend...");

  const selected = useMemo(
    () => candidates.find((candidate) => candidate.id === selectedId) ?? candidates[0],
    [candidates, selectedId]
  );

  const refresh = async (includeAll = screen !== "public") => {
    const next = includeAll ? await candidateApi.listAll() : await candidateApi.listPublic();
    setCandidates(next);
    setApiNotice("Connected to Bliss API. Admin actions use your Bliss session when available.");
    if (!next.some((candidate) => candidate.id === selectedId)) {
      setSelectedId(next[0]?.id ?? "");
    }
  };

  useEffect(() => {
    refresh(screen !== "public").catch(() => setApiNotice("Using local fallback data until the Bliss API is reachable."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  return (
    <main>
      <Hero screen={screen} setScreen={setScreen} />
      <section className="workspace phaseWorkspace" id="platform">
        <aside className="sidebar" aria-label="Phase 1 sections">
          <div className="sidebarTitle">Phase 2 MVP</div>
          {phaseScreens.map(([label, key, Icon]) => (
            <button
              className={screen === key ? "sideItem active" : "sideItem"}
              key={String(key)}
              onClick={() => setScreen(key as Screen)}
            >
              <Icon size={18} aria-hidden="true" />
              <span>{label}</span>
            </button>
          ))}
          <button className="sideItem" onClick={() => { candidateStore.reset(); refresh(); }}>
            <RefreshCcw size={18} aria-hidden="true" />
            <span>Reset demo data</span>
          </button>
        </aside>

        <div className="workMain">
          <p className="apiNotice">{apiNotice}</p>
          {screen === "public" && (
            <PublicProfiles
              candidates={candidates}
              selected={selected}
              setSelectedId={setSelectedId}
              refresh={() => refresh(false)}
            />
          )}
          {screen === "onboarding" && <CandidateOnboarding refresh={() => refresh(true)} setSelectedId={setSelectedId} />}
          {screen === "dashboard" && selected && <CandidateDashboard candidate={selected} refresh={() => refresh(true)} />}
          {screen === "admin" && <AdminReview candidates={candidates} refresh={() => refresh(true)} />}
          {screen === "donations" && selected && <DonationPreparation candidate={selected} />}
          {screen === "legal" && <LegalPlaceholders />}
        </div>
      </section>
      <ComplianceFlow />
    </main>
  );
}

function Hero({ screen, setScreen }: { screen: Screen; setScreen: (screen: Screen) => void }) {
  return (
    <section className="hero phaseHero">
      <div className="heroShade" />
      <nav className="nav" aria-label="Primary navigation">
        <div className="brand">
          <Landmark size={24} aria-hidden="true" />
          <span>CivicSignal</span>
        </div>
        <div className="navActions">
          <button className={screen === "public" ? "navButton active" : "navButton"} onClick={() => setScreen("public")}>
            Profiles
          </button>
          <button className={screen === "admin" ? "navButton active" : "navButton"} onClick={() => setScreen("admin")}>
            Review
          </button>
          <button className="button ghost" onClick={() => setScreen("onboarding")}>
            Launch candidate
          </button>
        </div>
      </nav>
      <div className="heroInner">
        <motion.div
          className="heroCopy"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <p className="eyebrow">Phase 2 payment foundation</p>
          <h1>CivicSignal</h1>
          <p className="heroText">
            Donation readiness, donor records, required attestations, fee math, and receipt placeholders now sit on top of the Bliss API and D1.
          </p>
          <div className="heroActions">
            <button className="button primary" onClick={() => setScreen("onboarding")}>
              Submit a candidate <ChevronRight size={18} aria-hidden="true" />
            </button>
            <button className="button secondary" onClick={() => setScreen("admin")}>
              Review queue
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function PublicProfiles({
  candidates,
  selected,
  setSelectedId,
  refresh
}: {
  candidates: CandidateProfile[];
  selected?: CandidateProfile;
  setSelectedId: (id: string) => void;
  refresh: () => void;
}) {
  const approved = candidates.filter((candidate) => candidate.status === "approved");
  const active = selected?.status === "approved" ? selected : approved[0];

  return (
    <>
      <header className="workHeader">
        <div>
          <p className="label">Public candidate profiles</p>
          <h2>Approved campaigns render from candidate data</h2>
        </div>
      </header>
      <div className="publicLayout">
        <div className="candidateGrid">
          {approved.map((candidate) => (
            <button className="candidatePanel candidateButton" key={candidate.id} onClick={() => setSelectedId(candidate.id)}>
              <CandidateSummary candidate={candidate} />
            </button>
          ))}
        </div>
        {active && <PublicCandidatePage candidate={active} refresh={refresh} />}
      </div>
    </>
  );
}

function CandidateSummary({ candidate }: { candidate: CandidateProfile }) {
  return (
    <>
      <div className="candidateTop">
        <div className="avatar" aria-hidden="true">{initials(candidate.name)}</div>
        <div>
          <h3>{candidate.name}</h3>
          <p>{candidate.office} {candidate.district}</p>
        </div>
        <StatusBadge status={candidate.status} />
      </div>
      <p className="quote">{candidate.quote}</p>
      <div className="tagRow">
        {candidate.priorities.map((priority) => <span key={priority}>{priority}</span>)}
      </div>
      <div className="metricRow">
        <div><strong>{candidate.supporterCount.toLocaleString()}</strong><span>Supporters</span></div>
        <div><strong>{candidate.party}</strong><span>Ballot identity</span></div>
        <div><strong>{candidate.location}</strong><span>Location</span></div>
      </div>
    </>
  );
}

function PublicCandidatePage({ candidate, refresh }: { candidate: CandidateProfile; refresh: () => void }) {
  const [message, setMessage] = useState("");

  const follow = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    candidateApi.follow(candidate.id, {
      name: String(form.get("name") ?? ""),
      email: String(form.get("email") ?? ""),
      zip: String(form.get("zip") ?? "")
    }).then(refresh);
    event.currentTarget.reset();
    setMessage("Supporter saved.");
  };

  return (
    <article className="publicProfile">
      <p className="label">/{candidate.slug}</p>
      <div className="publicProfileHeader">
        <div className="avatar largeAvatar" aria-hidden="true">{initials(candidate.name)}</div>
        <div>
          <h2>{candidate.name}</h2>
          <p>{candidate.office} {candidate.district} · {candidate.location}</p>
          <strong>{candidate.committeeName}</strong>
        </div>
      </div>
      <p className="profileStory">{candidate.story}</p>
      <div className="updates">
        {candidate.updates.map((update) => (
          <div className="updateItem" key={update.id}>
            <span>{update.date}</span>
            <strong>{update.title}</strong>
            <p>{update.body}</p>
          </div>
        ))}
      </div>
      <form className="supporterForm" onSubmit={follow}>
        <h3>Follow this campaign</h3>
        <input name="name" placeholder="Name" required />
        <input name="email" placeholder="Email" type="email" required />
        <input name="zip" placeholder="ZIP code" required />
        <button className="button full" type="submit">Follow campaign</button>
        {message && <p className="formMessage">{message}</p>}
      </form>
    </article>
  );
}

function CandidateOnboarding({ refresh, setSelectedId }: { refresh: () => void; setSelectedId: (id: string) => void }) {
  const [submission, setSubmission] = useState(emptySubmission);
  const [submitted, setSubmitted] = useState("");

  const update = (field: keyof CandidateSubmission, value: string) => {
    setSubmission((current) => ({ ...current, [field]: value }));
  };

  const updatePriority = (index: number, value: string) => {
    setSubmission((current) => ({
      ...current,
      priorities: current.priorities.map((priority, priorityIndex) => (priorityIndex === index ? value : priority))
    }));
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    candidateApi.submit({
      ...submission,
      priorities: submission.priorities.filter(Boolean)
    }).then((candidate) => {
      setSubmitted(`${candidate.name} is in the admin review queue.`);
      setSubmission(emptySubmission);
      setSelectedId(candidate.id);
      refresh();
    });
  };

  return (
    <>
      <header className="workHeader">
        <div>
          <p className="label">Candidate onboarding</p>
          <h2>Submit a campaign for review</h2>
        </div>
      </header>
      <form className="phaseForm" onSubmit={submit}>
        <fieldset>
          <legend>Candidate</legend>
          <input value={submission.name} onChange={(e) => update("name", e.target.value)} placeholder="Candidate name" required />
          <input value={submission.office} onChange={(e) => update("office", e.target.value)} placeholder="Office sought" required />
          <input value={submission.district} onChange={(e) => update("district", e.target.value)} placeholder="District or seat" required />
          <input value={submission.location} onChange={(e) => update("location", e.target.value)} placeholder="Location" required />
          <input value={submission.party} onChange={(e) => update("party", e.target.value)} placeholder="Party or nonpartisan" required />
        </fieldset>
        <fieldset>
          <legend>Committee</legend>
          <input value={submission.committeeName} onChange={(e) => update("committeeName", e.target.value)} placeholder="Committee name" required />
          <input value={submission.treasurerName} onChange={(e) => update("treasurerName", e.target.value)} placeholder="Treasurer name" required />
          <input value={submission.email} onChange={(e) => update("email", e.target.value)} placeholder="Campaign email" type="email" required />
        </fieldset>
        <fieldset className="wideFieldset">
          <legend>Public profile</legend>
          <textarea value={submission.story} onChange={(e) => update("story", e.target.value)} placeholder="Campaign story" required />
          <input value={submission.quote} onChange={(e) => update("quote", e.target.value)} placeholder="Candidate quote" required />
          <div className="threeColumnFields">
            {submission.priorities.map((priority, index) => (
              <input
                value={priority}
                onChange={(e) => updatePriority(index, e.target.value)}
                placeholder={`Priority ${index + 1}`}
                required={index === 0}
                key={index}
              />
            ))}
          </div>
          <input value={submission.updateTitle} onChange={(e) => update("updateTitle", e.target.value)} placeholder="First update title" required />
          <textarea value={submission.updateBody} onChange={(e) => update("updateBody", e.target.value)} placeholder="First update body" required />
        </fieldset>
        <button className="button primary" type="submit">Submit for review</button>
        {submitted && <p className="formMessage">{submitted}</p>}
      </form>
    </>
  );
}

function CandidateDashboard({ candidate, refresh }: { candidate: CandidateProfile; refresh: () => void }) {
  const [draft, setDraft] = useState(candidate);

  const save = (event: FormEvent) => {
    event.preventDefault();
    candidateApi.update(draft).then(refresh);
  };

  return (
    <>
      <header className="workHeader">
        <div>
          <p className="label">Candidate dashboard</p>
          <h2>Edit profile and review supporter totals</h2>
        </div>
      </header>
      <div className="dashboardLayout">
        <form className="phaseForm editForm" onSubmit={save}>
          <fieldset className="wideFieldset">
            <legend>Profile editor</legend>
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            <textarea value={draft.story} onChange={(e) => setDraft({ ...draft, story: e.target.value })} />
            <input value={draft.quote} onChange={(e) => setDraft({ ...draft, quote: e.target.value })} />
            <button className="button primary" type="submit">Save profile</button>
          </fieldset>
        </form>
        <div className="ledgerPanel">
          <div className="ledgerHeader">
            <div>
              <h3>{candidate.supporterCount.toLocaleString()} supporters</h3>
              <p>Status: {candidate.status.replace("_", " ")}</p>
            </div>
            <button className="smallButton"><Download size={16} aria-hidden="true" /> Export</button>
          </div>
          <div className="ledgerRow"><strong>Committee</strong><span>{candidate.committeeName}</span></div>
          <div className="ledgerRow"><strong>Treasurer</strong><span>{candidate.treasurerName}</span></div>
          <div className="ledgerRow"><strong>Public slug</strong><span>/{candidate.slug}</span></div>
        </div>
      </div>
    </>
  );
}

function AdminReview({ candidates, refresh }: { candidates: CandidateProfile[]; refresh: () => void }) {
  const review = (id: string, status: "approved" | "rejected") => {
    candidateApi
      .review(id, status, status === "approved" ? "Approved for public profile publishing." : "Needs follow-up before publishing.")
      .then(refresh);
  };

  return (
    <>
      <header className="workHeader">
        <div>
          <p className="label">Admin review queue</p>
          <h2>Approve or reject submitted profiles</h2>
        </div>
      </header>
      <div className="reviewPanel">
        {candidates.map((candidate) => (
          <div className="reviewRow reviewQueueRow" key={candidate.id}>
            <StatusBadge status={candidate.status} />
            <div>
              <strong>{candidate.name}</strong>
              <span>{candidate.committeeName} · {candidate.office} {candidate.district}</span>
            </div>
            <div className="reviewActions">
              <button className="smallButton" onClick={() => review(candidate.id, "approved")}>Approve</button>
              <button className="smallButton mutedButton" onClick={() => review(candidate.id, "rejected")}>Reject</button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

const emptyDonation: DonationSubmission = {
  amountCents: 10000,
  donorName: "",
  donorEmail: "",
  donorAddress: "",
  donorCity: "",
  donorState: "",
  donorZip: "",
  employer: "",
  occupation: "",
  attestsCitizenOrPermanentResident: false,
  attestsOwnFunds: false,
  attestsWithinLimits: false
};

function DonationPreparation({ candidate }: { candidate: CandidateProfile }) {
  const [donation, setDonation] = useState<DonationSubmission>(emptyDonation);
  const [readiness, setReadiness] = useState<DonationReadiness | null>(null);
  const [result, setResult] = useState<DonationIntentResult | null>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    candidateApi
      .donationReadiness(candidate.id)
      .then(setReadiness)
      .catch((error) => setMessage(error instanceof Error ? error.message : "Donation readiness unavailable."));
  }, [candidate.id]);

  const update = (field: keyof DonationSubmission, value: string | boolean | number) => {
    setDonation((current) => ({ ...current, [field]: value }));
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    candidateApi
      .prepareDonation(candidate.id, donation)
      .then((prepared) => {
        setResult(prepared);
        setMessage(
          prepared.checkoutUrl
            ? "Donation is ready for checkout."
            : "Donation record prepared. Checkout remains disabled until Stripe Connect is approved."
        );
      })
      .catch((error) => setMessage(error instanceof Error ? error.message : "Failed to prepare donation."));
  };

  const platformFee = Math.round((donation.amountCents * 210) / 10000);
  const stripeFee = Math.round(donation.amountCents * 0.029) + 30;
  const net = Math.max(0, donation.amountCents - platformFee - stripeFee);

  return (
    <>
      <header className="workHeader">
        <div>
          <p className="label">Phase 2 donation prep</p>
          <h2>Screen donors before checkout</h2>
        </div>
      </header>
      <div className="dashboardLayout">
        <form className="phaseForm editForm" onSubmit={submit}>
          <fieldset className="wideFieldset">
            <legend>Donation</legend>
            <input
              value={donation.amountCents / 100}
              min={1}
              step={1}
              type="number"
              onChange={(event) => update("amountCents", Math.round(Number(event.target.value) * 100))}
              placeholder="Donation amount"
              required
            />
            <input value={donation.donorName} onChange={(e) => update("donorName", e.target.value)} placeholder="Full legal name" required />
            <input value={donation.donorEmail} onChange={(e) => update("donorEmail", e.target.value)} placeholder="Email" type="email" required />
            <input value={donation.donorAddress} onChange={(e) => update("donorAddress", e.target.value)} placeholder="Street address" required />
            <div className="threeColumnFields">
              <input value={donation.donorCity} onChange={(e) => update("donorCity", e.target.value)} placeholder="City" required />
              <input value={donation.donorState} onChange={(e) => update("donorState", e.target.value)} placeholder="State" required />
              <input value={donation.donorZip} onChange={(e) => update("donorZip", e.target.value)} placeholder="ZIP" required />
            </div>
            <input value={donation.employer} onChange={(e) => update("employer", e.target.value)} placeholder="Employer" required />
            <input value={donation.occupation} onChange={(e) => update("occupation", e.target.value)} placeholder="Occupation" required />
            <label className="checkboxRow">
              <input type="checkbox" checked={donation.attestsCitizenOrPermanentResident} onChange={(e) => update("attestsCitizenOrPermanentResident", e.target.checked)} />
              I am a U.S. citizen or lawful permanent resident.
            </label>
            <label className="checkboxRow">
              <input type="checkbox" checked={donation.attestsOwnFunds} onChange={(e) => update("attestsOwnFunds", e.target.checked)} />
              I am donating my own funds and not on behalf of another person.
            </label>
            <label className="checkboxRow">
              <input type="checkbox" checked={donation.attestsWithinLimits} onChange={(e) => update("attestsWithinLimits", e.target.checked)} />
              My contribution is within applicable limits.
            </label>
            <button className="button primary" type="submit">Prepare donation record</button>
            {message && <p className="formMessage">{message}</p>}
          </fieldset>
        </form>
        <div className="ledgerPanel">
          <div className="ledgerHeader">
            <div>
              <h3>{candidate.name}</h3>
              <p>{readiness?.ready ? "Processor ready" : "Processor setup required"}</p>
            </div>
          </div>
          {(readiness?.blockers ?? []).map((blocker) => (
            <div className="ledgerRow" key={blocker}><strong>Blocker</strong><span>{blocker}</span></div>
          ))}
          <div className="ledgerRow"><strong>Donation</strong><span>{formatMoney(donation.amountCents)}</span></div>
          <div className="ledgerRow"><strong>Platform fee</strong><span>{formatMoney(platformFee)} (2.1%)</span></div>
          <div className="ledgerRow"><strong>Estimated Stripe fee</strong><span>{formatMoney(stripeFee)}</span></div>
          <div className="ledgerRow"><strong>Estimated net</strong><span>{formatMoney(net)}</span></div>
          {result && (
            <div className="ledgerRow"><strong>Donation status</strong><span>{result.donation.status.replace(/_/g, " ")}</span></div>
          )}
        </div>
      </div>
    </>
  );
}

function LegalPlaceholders() {
  return (
    <>
      <header className="workHeader">
        <div>
          <p className="label">Attorney review placeholders</p>
          <h2>Legal copy is visible before payments launch</h2>
        </div>
      </header>
      <div className="screenGrid">
        {[
          ["Candidate terms", "Candidate confirms authority to publish campaign information and maintain accurate committee records."],
          ["Supporter privacy", "Supporter information is collected for campaign updates and future compliance-reviewed donation workflows."],
          ["Donation disclaimer", "Payment language remains disabled until jurisdiction rules, contribution limits, and Stripe approval are finalized."]
        ].map(([title, text]) => (
          <article className="simplePanel" key={title}>
            <FileCheck2 size={22} aria-hidden="true" />
            <h3>{title}</h3>
            <p>{text}</p>
          </article>
        ))}
      </div>
    </>
  );
}

function ComplianceFlow() {
  return (
    <section className="compliance" id="compliance">
      <div className="sectionIntro">
        <p className="eyebrow">Phase 1 exit criteria</p>
        <h2>Profiles launch through a review gate</h2>
        <p>
          Candidate submissions, admin decisions, public profiles, profile edits, and supporter follows now run on one local data model.
        </p>
      </div>
      <div className="flowGrid">
        {complianceSteps.map(([title, text, Icon]) => (
          <motion.article
            className="flowStep"
            key={title}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.45 }}
          >
            <Icon size={24} aria-hidden="true" />
            <h3>{title}</h3>
            <p>{text}</p>
          </motion.article>
        ))}
      </div>
    </section>
  );
}

function StatusBadge({ status }: { status: CandidateProfile["status"] }) {
  const label = status.replace("_", " ");
  return (
    <span className={status === "approved" ? "status" : "status pending"}>
      <BadgeCheck size={15} aria-hidden="true" />
      {label}
    </span>
  );
}

const initials = (name: string) =>
  name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const formatMoney = (cents: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
